// @workflow assistant.approvals, assistant.attachments, assistant.send-message
/**
 * AVA Assistant event contract and conversation reducer.
 *
 * The UI is a pure function of a conversation built by folding a typed event stream: the aiva.api
 * agent loop's events over SSE, or a stored conversation's events replayed from history.
 * Contract doc: .claude/features/AVA_ASSISTANT_EVENT_CONTRACT.md (keep the two in step).
 *
 * Every function is immutable: it returns a new conversation and never changes its input.
 * No Vue or path-alias imports, so `npm run test:unit` (node --test) can load it directly.
 */
import { toolLabel } from './toolLabels.js';
import { RISK, REQUEST_KIND, REQUEST_STATUS, UI_REQUEST_KINDS, settledRequest } from './clientRequests.js';
import { cleanClarification, statusForAnswer } from './clarification.js';
import { cleanAnswer } from './clarificationAnswers.js';
import { updateMessage, upsertPart } from './conversationParts.js';
import { BLOCK_PART, blockPart } from './blocks.js';
import { cleanChanges } from './approvalChanges.js';
import { storedAttachments } from './attachments.js';

export { RISK, REQUEST_KIND, REQUEST_STATUS, UI_REQUEST_KINDS };

/** The contract major version this client understands; message.start carries the server's. */
export const CONTRACT_VERSION = 2;

const NEWER_CONTRACT_MESSAGE = 'AVA was updated. Reload the page to keep chatting.';

export const EVENT = Object.freeze({
  /** Stored only (never streamed): the user's turn, so a history replay shows the question. */
  USER_MESSAGE: 'user.message',
  MESSAGE_START: 'message.start',
  TEXT_DELTA: 'text.delta',
  TOOL_START: 'tool.start',
  TOOL_END: 'tool.end',
  TOOL_ERROR: 'tool.error',
  CLIENT_REQUEST: 'client.request',
  CLIENT_RESOLVED: 'client.resolved',
  MESSAGE_PAUSE: 'message.pause',
  MESSAGE_RESUME: 'message.resume',
  VISUAL: 'visual',
  ARTIFACT: 'artifact',
  LINKS: 'links',
  /** A reply block (contract "Reply blocks"): a number, table, record, breakdown, callout or suggestions. */
  BLOCK: 'block',
  NOTICE: 'notice',
  MESSAGE_END: 'message.end',
  ERROR: 'error',
});

export const PART = Object.freeze({
  TEXT: 'text',
  TOOLS: 'tools',
  APPROVAL: 'approval',
  /** A question to the user (contract "Clarifications"); the panel docks above the composer. */
  CLARIFICATION: 'clarification',
  VISUAL: 'visual',
  ARTIFACT: 'artifact',
  LINKS: 'links',
  BLOCK: BLOCK_PART,
});

export const MESSAGE_STATUS = Object.freeze({
  STREAMING: 'streaming',
  /** Paused on a client request (approval / UI tool); the stream has closed. */
  AWAITING: 'awaiting',
  DONE: 'done',
  STOPPED: 'stopped',
  FAILED: 'failed',
});

export const ROLE = Object.freeze({ USER: 'user', ASSISTANT: 'assistant', NOTICE: 'notice' });

export const CALL_STATUS = Object.freeze({
  RUNNING: 'running',
  COMPLETE: 'complete',
  ERROR: 'error',
  STOPPED: 'stopped',
});

const APPROVAL_RISKS = Object.freeze([RISK.MEDIUM, RISK.HIGH]);

/** @param {string} [id] */
export function createConversation(id = 'conversation') {
  return { id, messages: [], artifacts: { order: [], byId: {} }, requests: {}, cursors: {}, selectedVersions: {}, feedback: {}, error: null };
}

/**
 * A user turn. `segments` is what the bubble renders: plain text and @mention chips, never HTML.
 * @param {ReturnType<typeof createConversation>} conversation
 * `attachments`: the files sent with it, `{ id, name, kind, size, available? }` (the bubble's chips).
 * @param {{ id: string, text: string, segments?: Array<{ type: 'text'|'mention', text?: string, label?: string }>, at: string, context?: object, attachments?: Array }} message
 */
export function addUserMessage(conversation, { id, text, segments, at, context = null, attachments = [] }) {
  const message = {
    id,
    role: ROLE.USER,
    at,
    status: MESSAGE_STATUS.DONE,
    text,
    segments: segments && segments.length ? segments : [{ type: 'text', text }],
    context,
    attachments,
    parts: [],
  };
  return { ...conversation, messages: [...conversation.messages, message] };
}

/** Drops everything after the last user message (the failed or stopped reply), for "Try again". */
export function withoutTrailingReply(conversation) {
  let last = -1;
  conversation.messages.forEach((m, i) => {
    if (m.role === ROLE.USER) last = i;
  });
  if (last < 0) return { ...conversation, error: null };
  return { ...conversation, error: null, messages: conversation.messages.slice(0, last + 1) };
}

/* ---------- helpers ---------- */

function mapCalls(message, fn) {
  return {
    ...message,
    parts: message.parts.map((p) => (p.type === PART.TOOLS ? { ...p, calls: p.calls.map(fn) } : p)),
  };
}

function updateCall(message, callId, fn) {
  return mapCalls(message, (call) => (call.id === callId ? fn(call) : call));
}

const settleRunning = (status) => (call) => (call.status === CALL_STATUS.RUNNING ? { ...call, status } : call);

/* ---------- event handlers ---------- */

function onUserMessage(c, e) {
  if (!e.id || c.messages.some((m) => m.id === e.id)) return c;
  return addUserMessage(c, { id: e.id, text: e.text || '', at: e.at, context: e.context || null, attachments: storedAttachments(e.attachments) });
}

function onMessageStart(c, e) {
  if (c.messages.some((m) => m.id === e.messageId)) return c;
  if (e.contract > CONTRACT_VERSION) {
    const failed = { id: e.messageId, role: ROLE.ASSISTANT, at: e.at, status: MESSAGE_STATUS.FAILED, parts: [] };
    return { ...c, error: { message: NEWER_CONTRACT_MESSAGE, retryable: false }, messages: [...c.messages, failed] };
  }
  const message = { id: e.messageId, role: ROLE.ASSISTANT, at: e.at, status: MESSAGE_STATUS.STREAMING, parts: [], versionOf: e.versionOf || null, version: e.version || 1 };
  if (!e.versionOf) return { ...c, error: null, messages: [...c.messages, message] };
  // A new version of a reply (Try again) is the one shown: drop any older version the user picked.
  const { [e.versionOf]: dropped, ...selectedVersions } = c.selectedVersions || {};
  return { ...c, error: null, messages: [...c.messages, message], selectedVersions };
}

function onTextDelta(c, e) {
  return updateMessage(c, e.messageId, (m) =>
    upsertPart(
      m,
      e.partId,
      () => ({ id: e.partId, type: PART.TEXT, text: e.text }),
      (p) => ({ ...p, text: p.text + e.text })
    )
  );
}

function onToolStart(c, e) {
  const { verb, object } = toolLabel(e.tool, e.label);
  const call = { id: e.callId, tool: e.tool, verb, object, status: CALL_STATUS.RUNNING, durationMs: null, error: null };
  return updateMessage(c, e.messageId, (m) =>
    upsertPart(
      m,
      e.partId,
      () => ({ id: e.partId, type: PART.TOOLS, calls: [call] }),
      (p) => ({ ...p, calls: [...p.calls, call] })
    )
  );
}

function onToolEnd(c, e) {
  return updateMessage(c, e.messageId, (m) =>
    updateCall(m, e.callId, (call) => ({ ...call, status: CALL_STATUS.COMPLETE, durationMs: e.durationMs ?? null }))
  );
}

function onToolError(c, e) {
  return updateMessage(c, e.messageId, (m) =>
    updateCall(m, e.callId, (call) => ({ ...call, status: CALL_STATUS.ERROR, error: e.message || null }))
  );
}

/**
 * A client request (contract v2): the server needs the user (approval) or the UI (ui.action /
 * ui.query). Every request is recorded in `conversation.requests`; approvals and valid
 * clarifications add a visible part. An invalid clarification is recorded with `invalid` (the
 * reason) and no part, so the UI answers it dismissed and never shows it.
 */
function onClientRequest(c, e) {
  const clean = e.kind === REQUEST_KIND.CLARIFICATION ? cleanClarification(e) : null;
  const request = { requestId: e.requestId, kind: e.kind, messageId: e.messageId, expiresAt: e.expiresAt || null, action: e.action || null, query: e.query || null, status: REQUEST_STATUS.PENDING, ...(clean && !clean.ok ? { invalid: clean.reason } : {}) };
  const withRequest = updateMessage(c, e.messageId, (m) => {
    const base = { id: e.partId, requestId: e.requestId, status: REQUEST_STATUS.PENDING };
    let part = null;
    if (e.kind === REQUEST_KIND.APPROVAL) part = { ...base, type: PART.APPROVAL, title: e.title, summary: e.summary, fields: e.fields || [], changes: cleanChanges(e.changes), confirmLabel: e.confirmLabel, risk: APPROVAL_RISKS.includes(e.risk) ? e.risk : RISK.MEDIUM };
    if (clean && clean.ok) part = { ...base, type: PART.CLARIFICATION, ...clean.value, expiresAt: request.expiresAt, answer: null };
    return part ? upsertPart(m, e.partId, () => part, () => part) : m;
  });
  if (withRequest === c) return c;
  return { ...withRequest, requests: { ...c.requests, [e.requestId]: request } };
}

function setRequestStatus(c, requestId, status) {
  const messages = c.messages.map((m) => {
    if (!m.parts.some((p) => p.requestId === requestId)) return m;
    return { ...m, parts: m.parts.map((p) => (p.requestId === requestId ? { ...p, status } : p)) };
  });
  const request = c.requests[requestId];
  return { ...c, messages, requests: request ? { ...c.requests, [requestId]: settledRequest(request, status) } : c.requests };
}

/**
 * A clarification's `client.resolved` may echo the answer (`answer`): the thread line and a history
 * replay read it. Without one, the answer the UI recorded when the user answered stays.
 */
function withEchoedAnswer(c, e) {
  const isTarget = (p) => p.requestId === e.requestId && p.type === PART.CLARIFICATION;
  const withAnswer = (p) => {
    const answer = isTarget(p) ? cleanAnswer(e.answer, p.questions) : null;
    return answer ? { ...p, answer, status: statusForAnswer(answer) } : p;
  };
  return { ...c, messages: c.messages.map((m) => (m.parts.some(isTarget) ? { ...m, parts: m.parts.map(withAnswer) } : m)) };
}

function onClientResolved(c, e) {
  if (!c.requests[e.requestId] && !c.messages.some((m) => m.parts.some((p) => p.requestId === e.requestId))) return c;
  const resolved = setRequestStatus(c, e.requestId, e.outcome);
  return e.answer ? withEchoedAnswer(resolved, e) : resolved;
}

function onMessagePause(c, e) {
  return updateMessage(c, e.messageId, (m) => (m.status === MESSAGE_STATUS.STREAMING ? { ...m, status: MESSAGE_STATUS.AWAITING } : m));
}

function onMessageResume(c, e) {
  return updateMessage(c, e.messageId, (m) => (m.status === MESSAGE_STATUS.AWAITING ? { ...m, status: MESSAGE_STATUS.STREAMING } : m));
}

/** When a reply ends, any request it still waited on can no longer be answered. */
function closePendingRequests(c, messageId) {
  return Object.values(c.requests)
    .filter((r) => r.messageId === messageId && r.status === REQUEST_STATUS.PENDING)
    .reduce((acc, r) => setRequestStatus(acc, r.requestId, REQUEST_STATUS.UNANSWERED), c);
}

function registerArtifact(c, artifact) {
  const known = c.artifacts.order.includes(artifact.id);
  return {
    ...c,
    artifacts: {
      order: known ? c.artifacts.order : [...c.artifacts.order, artifact.id],
      byId: { ...c.artifacts.byId, [artifact.id]: artifact },
    },
  };
}

/** A visual may carry `artifact`: its chart then shows in the files list, without a second card. */
function onVisual(c, e) {
  const artifactId = (e.artifact && e.artifact.id) || e.artifactId || null;
  const part = { id: e.partId, type: PART.VISUAL, visualId: e.visualId, artifactId, spec: e.spec };
  const withPart = updateMessage(c, e.messageId, (m) => upsertPart(m, e.partId, () => part, () => part));
  return withPart !== c && e.artifact ? registerArtifact(withPart, e.artifact) : withPart;
}

function onArtifact(c, e) {
  const { artifact } = e;
  const withPart = updateMessage(c, e.messageId, (m) =>
    upsertPart(
      m,
      e.partId,
      () => ({ id: e.partId, type: PART.ARTIFACT, artifactId: artifact.id }),
      (p) => p
    )
  );
  return withPart === c ? c : registerArtifact(withPart, artifact);
}

function onLinks(c, e) {
  const part = { id: e.partId, type: PART.LINKS, links: e.links || [] };
  return updateMessage(c, e.messageId, (m) => upsertPart(m, e.partId, () => part, () => part));
}

/** A block that fails its checks is kept with its reason and never drawn (blocks.js); the text still shows. */
const onBlock = (c, e) => updateMessage(c, e.messageId, (m) => upsertPart(m, e.partId, () => blockPart(e), () => blockPart(e)));

function onNotice(c, e) {
  const message = { id: e.id, role: ROLE.NOTICE, at: e.at, status: MESSAGE_STATUS.DONE, text: e.text, parts: [] };
  return { ...c, messages: [...c.messages, message] };
}

function onMessageEnd(c, e) {
  const ended = updateMessage(c, e.messageId, (m) => {
    if (e.stopped) return { ...mapCalls(m, settleRunning(CALL_STATUS.STOPPED)), status: MESSAGE_STATUS.STOPPED };
    return { ...m, status: MESSAGE_STATUS.DONE };
  });
  return ended === c ? c : closePendingRequests(ended, e.messageId);
}

const failMessage = (m) => ({ ...mapCalls(m, settleRunning(CALL_STATUS.ERROR)), status: MESSAGE_STATUS.FAILED });

/** With a messageId only that reply fails; without one (a transport error) every streaming reply does. */
function onError(c, e) {
  const failed = e.messageId
    ? updateMessage(c, e.messageId, failMessage)
    : { ...c, messages: c.messages.map((m) => (m.status === MESSAGE_STATUS.STREAMING ? failMessage(m) : m)) };
  return { ...failed, error: { message: e.message, retryable: !!e.retryable } };
}

const HANDLERS = Object.freeze({
  [EVENT.USER_MESSAGE]: onUserMessage,
  [EVENT.MESSAGE_START]: onMessageStart,
  [EVENT.TEXT_DELTA]: onTextDelta,
  [EVENT.TOOL_START]: onToolStart,
  [EVENT.TOOL_END]: onToolEnd,
  [EVENT.TOOL_ERROR]: onToolError,
  [EVENT.CLIENT_REQUEST]: onClientRequest,
  [EVENT.CLIENT_RESOLVED]: onClientResolved,
  [EVENT.MESSAGE_PAUSE]: onMessagePause,
  [EVENT.MESSAGE_RESUME]: onMessageResume,
  [EVENT.VISUAL]: onVisual,
  [EVENT.ARTIFACT]: onArtifact,
  [EVENT.LINKS]: onLinks,
  [EVENT.BLOCK]: onBlock,
  [EVENT.NOTICE]: onNotice,
  [EVENT.MESSAGE_END]: onMessageEnd,
  [EVENT.ERROR]: onError,
});

const hasSeq = (e) => typeof e.runId === 'string' && Number.isInteger(e.seq);

/**
 * Folds one event into the conversation. Unknown or malformed events return the same object,
 * so a newer server can add event types without breaking an older client.
 * Server events carry `runId` + `seq`: an event at or below the run's cursor was already applied
 * (a reconnect replays from the last seq), so it is ignored. Events without a seq (the client's
 * own stopped message.end) always apply.
 */
export function applyEvent(conversation, event) {
  if (!event) return conversation;
  const cursors = conversation.cursors || {};
  if (hasSeq(event) && event.seq <= (cursors[event.runId] || 0)) return conversation;
  const handler = HANDLERS[event.type];
  const next = handler ? handler(conversation, event) : conversation;
  return hasSeq(event) ? { ...next, cursors: { ...cursors, [event.runId]: event.seq } } : next;
}

/* ---------- selectors ---------- */

/** The last seq applied for a run: where a reconnect resumes from (0 = nothing yet). */
export const lastSeq = (c, runId) => (c.cursors && c.cursors[runId]) || 0;

export const selectArtifacts = (c) => c.artifacts.order.map((id) => c.artifacts.byId[id]);

export const isStreaming = (c) => c.messages.some((m) => m.status === MESSAGE_STATUS.STREAMING);

/** A reply is paused waiting on the user or the UI. */
export const isAwaiting = (c) => c.messages.some((m) => m.status === MESSAGE_STATUS.AWAITING);

/** UI tool requests (ui.action / ui.query) the UI still has to execute and answer. */
export const pendingUiRequests = (c) =>
  Object.values(c.requests || {}).filter((r) => UI_REQUEST_KINDS.includes(r.kind) && r.status === REQUEST_STATUS.PENDING);

/** The note under a question the user stopped before AVA started its reply. */
export const STOPPED_BEFORE_REPLY = 'Stopped before AVA answered';

/** The last turn is the user's: a reply was asked for but hasn't started yet. */
export const awaitsFirstReply = (c) => c.messages.length > 0 && c.messages[c.messages.length - 1].role === ROLE.USER;

/** The reply started (message.start) but has no text or step to show yet: the UI keeps saying "Thinking…". */
export function streamingWithoutContent(c) {
  const last = c.messages[c.messages.length - 1];
  return !!last && last.role === ROLE.ASSISTANT && last.status === MESSAGE_STATUS.STREAMING && last.parts.length === 0;
}

/**
 * Stop pressed before message.start: no reply exists to mark stopped, so a notice says so.
 * A reply that already started is stopped by its own message.end instead (unchanged here).
 */
export function withStoppedBeforeReply(c, { id, at }) {
  return awaitsFirstReply(c) ? applyEvent(c, { type: EVENT.NOTICE, id, text: STOPPED_BEFORE_REPLY, at }) : c;
}

export function lastUserText(c) {
  for (let i = c.messages.length - 1; i >= 0; i -= 1) {
    if (c.messages[i].role === ROLE.USER) return c.messages[i].text;
  }
  return null;
}
